import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, BookOpen, Brain, X, Zap } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../lib/apiClient';

interface AlertCopy {
  title: string;
  body: string;
  primaryCta: string;
  primaryAction: 'run-manual' | 'read-analysis';
  secondaryCta?: string;
  secondaryAction?: 'enable-auto';
}

const MORNING_OFF: AlertCopy[] = [
  {
    title: 'Análise do dia não foi gerada',
    body: 'Seu Mentor analisou o sistema inteiro durante a noite — mas você desativou a entrega automática. Os dados estão prontos. Quando quiser saber o que seus números estão tentando te dizer, é só rodar a análise.',
    primaryCta: 'Rodar Meu Mentor agora',
    primaryAction: 'run-manual',
    secondaryCta: 'Reativar modo automático',
    secondaryAction: 'enable-auto',
  },
  {
    title: 'Seus dados mudaram enquanto você dormia',
    body: 'Enquanto você dormia, muita coisa aconteceu nos seus dados. Contratos, pagamentos, prazos e custos — tudo tem uma história pra contar. Seu Mentor já sabe qual é. Você ainda não.',
    primaryCta: 'Ver minha análise',
    primaryAction: 'run-manual',
    secondaryCta: 'Reativar entrega automática',
    secondaryAction: 'enable-auto',
  },
  {
    title: 'Mentor pausado — monitoramento inativo',
    body: 'Existe uma diferença entre achar que a operação está indo bem e saber que está. Seu Mentor tem a leitura de hoje pronta. Falta só você pedir.',
    primaryCta: 'Rodar análise agora',
    primaryAction: 'run-manual',
    secondaryCta: 'Reativar automático às 7h',
    secondaryAction: 'enable-auto',
  },
];

const AFTERNOON_OFF: AlertCopy[] = [
  {
    title: 'Sem visibilidade da operação hoje',
    body: 'Já passa da metade do dia e você ainda não viu o que está acontecendo na empresa hoje. Seus números mudaram desde ontem — e nem toda mudança é boa. Seu Mentor tem observações pendentes que você ainda não leu.',
    primaryCta: 'Ver agora',
    primaryAction: 'run-manual',
    secondaryCta: 'Reativar análise automática',
    secondaryAction: 'enable-auto',
  },
  {
    title: 'Possíveis alertas pendentes',
    body: 'Um problema pequeno de manhã vira um problema caro à noite. Seu Mentor identificou pontos que merecem sua atenção — mas a análise ainda não foi rodada hoje. Você está operando no escuro.',
    primaryCta: 'Rodar análise agora',
    primaryAction: 'run-manual',
    secondaryCta: 'Reativar automático',
    secondaryAction: 'enable-auto',
  },
];

const UNREAD_ANALYSIS: AlertCopy[] = [
  {
    title: 'Análise do dia aguardando leitura',
    body: 'Seu Mentor rodou às 7h e encontrou pontos que precisam da sua atenção. A análise está esperando desde cedo. Quanto antes você ler, mais tempo tem pra agir hoje.',
    primaryCta: 'Ler minha análise',
    primaryAction: 'read-analysis',
  },
  {
    title: 'Ações prioritárias identificadas',
    body: 'Tem uma análise completa da sua operação que você ainda não abriu. Seu Mentor identificou ações prioritárias pra hoje — e o dia já está andando.',
    primaryCta: 'Abrir análise do dia',
    primaryAction: 'read-analysis',
  },
];

const ROTATION_SEQUENCE = [0, 2, 1];
const STORAGE_KEY = '@helps:mentor-alert-dismissed';

type AlertScenario = 'morning_off' | 'afternoon_off' | 'unread' | null;

interface MentorStatusForAlert {
  autoRunEnabled: boolean;
  hasRunToday: boolean;
  analysisReadToday: boolean;
}

function getVariationIndex() {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / 86400000);
  return ROTATION_SEQUENCE[dayOfYear % ROTATION_SEQUENCE.length];
}

function determineScenario(status: MentorStatusForAlert): AlertScenario {
  const now = new Date();
  const hour = now.getHours();

  if (!status.autoRunEnabled) {
    return hour < 12 ? 'morning_off' : 'afternoon_off';
  }

  if (status.hasRunToday && !status.analysisReadToday && hour >= 11) {
    return 'unread';
  }

  return null;
}

function getCopy(scenario: AlertScenario): AlertCopy | null {
  if (!scenario) return null;
  const index = getVariationIndex();
  switch (scenario) {
    case 'morning_off':
      return MORNING_OFF[index % MORNING_OFF.length];
    case 'afternoon_off':
      return AFTERNOON_OFF[index % AFTERNOON_OFF.length];
    case 'unread':
      return UNREAD_ANALYSIS[index % UNREAD_ANALYSIS.length];
    default:
      return null;
  }
}

function getDismissedToday() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { date: string; scenario: string };
    const today = new Date().toISOString().slice(0, 10);
    return parsed.date === today ? parsed.scenario : null;
  } catch {
    return null;
  }
}

function setDismissedToday(scenario: string) {
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: today, scenario }));
}

export function MentorAlertBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<MentorStatusForAlert | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [enablingAuto, setEnablingAuto] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<MentorStatusForAlert>('/mentor/status');
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    setDismissed(getDismissedToday());
    loadStatus();
    const timer = window.setInterval(loadStatus, 60_000);
    return () => window.clearInterval(timer);
  }, [loadStatus]);

  const scenario = useMemo(
    () => (status ? determineScenario(status) : null),
    [status],
  );
  const copy = useMemo(() => getCopy(scenario), [scenario]);

  if (!copy || !scenario || dismissed === scenario || location.pathname === '/mentor') {
    return null;
  }

  const handlePrimary = async () => {
    if (copy.primaryAction === 'read-analysis') {
      navigate('/mentor');
      return;
    }

    setRunning(true);
    try {
      await api.post('/mentor/analyze');
      toast.success('Análise do Meu Mentor gerada com sucesso.');
      navigate('/mentor');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao rodar o Meu Mentor.');
    } finally {
      setRunning(false);
    }
  };

  const handleEnableAuto = async () => {
    setEnablingAuto(true);
    try {
      await api.post('/mentor/quick-enable-auto');
      toast.success('Modo automático reativado.');
      await loadStatus();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha ao reativar o modo automático.');
    } finally {
      setEnablingAuto(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 pt-4">
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="mt-0.5 w-11 h-11 rounded-2xl bg-white border border-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm shrink-0">
              {scenario === 'unread' ? <BookOpen className="w-5 h-5" /> : scenario === 'afternoon_off' ? <AlertTriangle className="w-5 h-5" /> : <Brain className="w-5 h-5" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">{copy.title}</p>
              <p className="text-sm text-emerald-900/75 mt-1 leading-relaxed">{copy.body}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePrimary}
              disabled={running}
              className="rounded-2xl bg-brand-dark text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand-dark/90 disabled:opacity-60 inline-flex items-center gap-2"
            >
              {running ? <Zap className="w-4 h-4 animate-pulse" /> : <Brain className="w-4 h-4" />}
              {copy.primaryCta}
            </button>

            {copy.secondaryAction === 'enable-auto' && copy.secondaryCta ? (
              <button
                onClick={handleEnableAuto}
                disabled={enablingAuto}
                className="rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
              >
                {copy.secondaryCta}
              </button>
            ) : null}

            <button
              onClick={() => {
                setDismissedToday(scenario);
                setDismissed(scenario);
              }}
              className="rounded-full p-2 text-emerald-500 hover:bg-white/70 hover:text-emerald-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

